/**
 * Griha pravesh decorations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_decor_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const grihaPraveshDecorationsPack: ToilePack = {
    slug: 'griha-pravesh-decorations',
    category: 'occasions',
    name: 'Griha pravesh decorations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'door', label: 'Door toran', subject: 'a front door with a marigold and mango leaf toran'},
        {key: 'rangoli', label: 'Rangoli', subject: 'a rangoli at the threshold with diyas'},
        {key: 'kalash', label: 'Kalash', subject: 'a decorated kalash with a coconut'},
    ],
    styles: [
        {key: 'marigold', label: 'Marigold', style: 'traditional decoration of fresh marigolds and mango leaves with brass and clay pieces, warm morning sun from camera-left, a bright warm balance with crisp short shadows', palette: 'saffron orange, yellow and leaf green with brass gold', framing: 'eye-level close view centered on the arrangement'},
        {key: 'minimal', label: 'Modern minimal', style: 'modern minimal decoration with white flowers, a few brass diyas and clean marble surfaces, soft daylight from overhead, an airy pale balance with faint shadows', palette: 'white, ivory, soft green and brass', framing: 'straight-on view with plenty of empty space around'},
        {key: 'evening', label: 'Diya glow', style: 'evening decoration lit by many clay diyas and string lights, warm glow from below and camera-right, a dark warm balance with glowing highlights', palette: 'deep amber, red and gold', framing: 'three-quarter view, the setup glowing against a dim entrance'},
    ],
    directions: ['south indian', 'apartment door', 'lotus theme'],
    directionHint: 'try: south indian',
    seo: {
        title: 'Griha pravesh decoration: door, rangoli, kalash',
        metaDescription: 'Nine griha pravesh decoration pictures for a housewarming: a door toran, a rangoli and a kalash, in marigold, modern and diya glow looks.',
        h1: 'Griha pravesh decoration: door toran, rangoli, kalash',
        tagline: 'Nine pictures, three housewarming pieces, each in a traditional marigold, a modern minimal and a diya glow look.',
        intro: 'Griha pravesh decoration for a housewarming in an Indian home: a front door with a marigold and mango leaf toran, a rangoli at the threshold with diyas, and a decorated kalash with a coconut. Each piece appears in a traditional look of marigolds and brass, a modern minimal look of white flowers and marble, and an evening look lit by clay diyas. Type a twist, such as south indian or lotus theme, and the same pieces carry it.',
        useCases: [
            'a decorated front door for a housewarming',
            'a rangoli at the threshold for guests',
            'a kalash at the entrance',
            'a diya-lit entrance in the evening',
        ],
        faq: [
            {q: 'What are the three looks?', a: 'A traditional look of saffron marigolds, mango leaves and brass; a modern minimal look of white flowers and marble; and an evening look of clay diyas and string lights.'},
            {q: 'What is the kalash?', a: 'A decorated pot with a coconut on top, shown in each of the three looks.'},
            {q: 'Are there any words in the pictures?', a: 'No. The pictures have no text.'},
            {q: 'Can I ask for a South Indian style?', a: 'Yes. Type a twist such as south indian or apartment door, and the same pieces carry it.'},
        ],
    },
}
